import storeManager from '../../store/StoreManager';
import objectTypes from '../../constants/ObjectTypes';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import questionReader from '../../store/readHelpers/QuestionReader';
import visitedQuestionsReader from '../../store/readHelpers/VisitedQuestionsReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import tocReader from './TocReader';
import responseService from '../../services/ResponseService';
import constants from '../../qti/Constants';
import isDrawingResponseIdentifier from '../../services/DrawingUtils';
import DrawingCanvasService from '../../services/DrawingCanvasService';
import utilService from '../../services/UtilService';

const responseReader = {
    isAtLeastOneInteractionRepliedTo(contentRevisionId, { response } = storeManager.getState()) {
        if (!response.status) {
            return false;
        }
        const itemStatus = response.status[contentRevisionId];
        return !!itemStatus && itemStatus.atLeastOneInteractionRepliedTo;
    },

    areAllInteractionRepliedTo(contentRevisionId) {
        const { response } = storeManager.getState();
        if (!response.status) {
            return false;
        }
        const itemStatus = response.status[contentRevisionId];
        return !!itemStatus?.allDeclarationsRepliedTo;
    },

    /**
     * Checks if any response identifier for a given question has an empty response.
     *
     * @param {number} contentRevisionId
     * @returns {boolean}
     */
    isAnyResponseEmpty(contentRevisionId) {
        const itemResult = this.getItemResultForQuestion(contentRevisionId);
        if (!itemResult || !itemResult.responseIdentifiers) {
            return true;
        }

        const question = assessmentReader.getQuestion(contentRevisionId);
        const responseDeclarations = question?.responseDeclarations || [];

        const isDrawingQuestionType = question?.responseModels?.[0]?.type === constants.interactionType.DRAWING_INTERACTION &&
        question?.responseModels?.length === 1;

        return itemResult.responseIdentifiers.some((responseIdentifier) => {
            const { identifier, userResponse } = responseIdentifier;

            if (identifier === 'HINTREQUEST') {
                return false;
            }

            if (isDrawingResponseIdentifier(identifier)) {
                return false;
            }

            const isDrawingBaseIdentifier = DrawingCanvasService.isDrawingBaseIdentifier(identifier, question);

            // Skip base drawing identifier only for non-drawing questions
            if (isDrawingBaseIdentifier && !isDrawingQuestionType) {
                return false;
            }

            if (!userResponse || userResponse.length === 0) {
                return true;
            }

            const hasNonEmptyResponse = userResponse.some((responseItem) => {
                if (responseItem === null || responseItem === undefined) {
                    return false;
                }
                if (isDrawingQuestionType && isDrawingBaseIdentifier) {
                    if (typeof responseItem === 'string') {
                        return responseItem.trim() !== '' && !responseService.emptyResponses.includes(responseItem);
                    }
                    return true;
                }
                return !responseService.emptyResponses.some(emptyItem => responseItem === emptyItem);
            });

            if (!hasNonEmptyResponse) {
                return true;
            }

            const declaration = responseDeclarations.find(d => d.identifier === identifier);
            if (declaration?.defaultValue && utilService.arraysHaveIdenticalValues(userResponse, declaration.defaultValue)) {
                return true;
            }

            return false;
        });
    },

    /**
     * Returns the number of answered questions in all sections from the state, not counting section stimulus
     * @param {object} state
     * @returns {int}
     */
    getTotalNumberOfResponses(state = storeManager.getState()) {
        const questionKeys = Object.getOwnPropertyNames(state.response);
        let count = 0;

        questionKeys.forEach((questionKey) => {
            const contentRevisionId = parseInt(questionKey, 10);
            if (isNaN(contentRevisionId)) {
                return;
            }

            const question = assessmentReader.getQuestion(contentRevisionId);
            if (
                tocReader.isSectionStimulus(contentRevisionId) ||
                questionReader.isForm(question) ||
                questionReader.isContentBlock(question)
            ) {
                return;
            }
            let repliedTo = false;
            if (
                (question && (question.objectType === objectTypes.ORAL)) ||
                (question && questionReader.isSpreadSheetQuestion(question))
            ) {
                repliedTo = responseReader.isQuestionAttempted(question);
            } else {
                repliedTo = this.isAtLeastOneInteractionRepliedTo(contentRevisionId, state);
            }

            if (repliedTo) {
                count++;
            }
        });

        return count;
    },

    /**
     * Gets the itemResult for a given question.
     *
     * @param contentRevisionId
     * @returns {object | undefined}
     */
    getItemResultForQuestion(contentRevisionId) {
        const state = storeManager.getState();

        return state.response[contentRevisionId];
    },

    /**
     * Returns true when there is a non empty user response for at least one of the interactions in the question.
     * For objectTypes.DOCUMENT it always returns true.
     * For objectTypes.ORAL and objectTypes.SPREADSHEET it returns true if the question is visited, otherwise false.
     *
     * @param {object} question
     * @param {object} state
     * @returns {boolean}
     */
    isQuestionAttempted(question, state = storeManager.getState()) {
        const contentRevisionId = question.contentRevisionId;
        const isViewContext = sessionReader.isContextAnyViewDeliveryType();

        if (questionReader.isDocument(question) || questionReader.isContentBlock(question)) {
            return true;
        }

        if (questionReader.isOralQuestion(question) || questionReader.isSpreadSheetQuestion(question)) {
            return isViewContext || visitedQuestionsReader.isQuestionVisited(contentRevisionId, state);
        }
        return this.isAtLeastOneInteractionRepliedTo(contentRevisionId, state);
    },

    /**
     * Returns true if the question has at least one non-empty text response
     * on its text-like responseDeclarations.
     *
     * For form question, this specifically checks RESPONSE-1 (baseType "string")
     * and ignores RESPONSE-2 (baseType "identifier").
     *
     * @param {number} contentRevisionId
     * @returns {boolean}
     */
    hasNonEmptyTextResponse(contentRevisionId) {
        const itemResult = this.getItemResultForQuestion(contentRevisionId);
        if (!itemResult || !itemResult.responseIdentifiers) {
            return false;
        }

        const question = assessmentReader.getQuestion(contentRevisionId);
        if (!question) {
            return false;
        }

        const emptyResponses = responseService.emptyResponses;

        // Only consider text-like response identifiers for this question
        const textResponseIds = this.getTextResponseIdentifiersForQuestion(question);
        if (!textResponseIds || textResponseIds.length === 0) {
            return false;
        }

        return itemResult.responseIdentifiers.some((ri) => {
            if (!textResponseIds.includes(ri.identifier)) {
                return false;
            }

            if (!Array.isArray(ri.userResponse) || ri.userResponse.length === 0) {
                return false;
            }

            return ri.userResponse.some(val => !emptyResponses.includes(val));
        });
    },

    /**
     * For a given question, returns the identifiers of responseDeclarations
     * that are "text-like" (baseType "string").
     *
     * This works for composite/form questions with mixed interactions, e.g.
     * RESPONSE-1 (textEntryInteraction, baseType "string")
     * RESPONSE-2 (choiceInteraction, baseType "identifier")
     *
     * @param {object} question
     * @returns {string[]} array of response identifiers
     */
    getTextResponseIdentifiersForQuestion(question) {
        if (!question || !Array.isArray(question.responseDeclarations)) {
            return [];
        }

        const textIds = [];

        question.responseDeclarations.forEach((decl) => {
            if (decl.baseType === 'string') {
                if (decl.identifier) {
                    textIds.push(decl.identifier);
                }
            }
        });

        return textIds;
    },

    /**
     * Returns true when the question is fully answered.
     * For most questions this means all interactions are replied to.
     * For MANDATORY questions, it also requires that no response is empty.
     * For DOCUMENT/CONTENT_BLOCK it always returns true.
     * For ORAL/SPREADSHEET it returns true if the question is visited (or in view context).
     *
     * @param {object} question
     * @returns {boolean}
     */
    isQuestionDone(question) {
        if (!question) {
            console.error(`Question does not exist, responseReader.isQuestionDone will return false: ${question}`);
            return false;
        }

        const state = storeManager.getState();
        const contentRevisionId = question.contentRevisionId;
        const isViewContext = sessionReader.isContextAnyViewDeliveryType();

        if (questionReader.isDocument(question) || questionReader.isContentBlock(question)) {
            return true;
        }

        if (questionReader.isOralQuestion(question) || questionReader.isSpreadSheetQuestion(question)) {
            return isViewContext || visitedQuestionsReader.isQuestionVisited(contentRevisionId, state);
        }

        const allInteractionsReplied = this.areAllInteractionRepliedTo(contentRevisionId, state);

        if (question.isMandatoryQuestion) {
            return allInteractionsReplied && !this.isAnyResponseEmpty(contentRevisionId, state);
        }

        return allInteractionsReplied;
    },

    /**
     * Returns true if the question contains any text-like responseDeclaration
     * (e.g., baseType "string"). Useful for FORM and mixed-type items.
     *
     * @param {object} question
     * @returns {boolean}
     */
    questionHasTextInteraction(question) {
        const textIds = this.getTextResponseIdentifiersForQuestion(question);
        return Array.isArray(textIds) && textIds.length > 0;
    },

    getResultsFromResponses(originalResponses, { response } = storeManager.getState()) {
        const results = {};
        Object.keys(originalResponses).forEach((key) => {
            try {
                const intKey = parseInt(key, 10);
                const originalResponse = originalResponses[key];
                const parsedResponse = response[intKey];
                const { manualScore, score } = originalResponse;
                const { outcomeVariables } = parsedResponse;
                const resData = {
                    manualScore,
                    score,
                };

                if (outcomeVariables &&
                    outcomeVariables.FEEDBACK &&
                    outcomeVariables.FEEDBACK.value !== undefined) {
                    resData.autoFeedback = outcomeVariables.FEEDBACK.value;
                }

                results[intKey] = resData;
            } catch (error) {
                window.console.error(error);
            }
        });

        return results;
    },

    getResponses(state = storeManager.getState()) {
        return Object
            .entries(state.response)
            .filter(entry => entry[1].identifier !== undefined)
            .map(entry => entry[1]);
    },

    getResponsesForAssessmentRunId(assessmentRunId, state = storeManager.getState()) {
        return this
            .getResponses(state)
            .filter(response => response.assessmentRunId === assessmentRunId);
    },

    getFirstUncommittedQuestion() {
        const { toc: { itemsOrder } } = storeManager.getState();
        return itemsOrder.find((item) => {
            const questionResponse = this.getItemResultForQuestion(item.contentRevisionId);
            return responseService.isQuestionAnswerCommittingUnattempted(questionResponse);
        });
    },

    areAllQuestionsCommitted() {
        const { toc: { itemsOrder } } = storeManager.getState();
        return !!itemsOrder && itemsOrder.length > 0 && itemsOrder.every((item) => {
            const questionResponse = this.getItemResultForQuestion(item.contentRevisionId);
            // TEII-5241 - question responses that are either stored or pending will be considered as committed,
            // to avoid taking TT to previous answered questions.
            return responseService.isQuestionCommitAnswerStored(questionResponse) || responseService.isQuestionCommitAnswerPending(questionResponse);
        });
    },

    userResponseHasIndentation() {
        const { response } = storeManager.getState();
        return !!response && Object.values(response).some(responseItem => !!responseItem?.hasIndentation);
    },

    userResponseHasAlignment() {
        const { response } = storeManager.getState();
        return !!response && Object.values(response).some(responseItem => !!responseItem?.hasAlignment);
    },
};

export default responseReader;
